// Shared formatting for the pricing board. Money from PriceLabs is whole dollars; money from Hospitable is minor units.

export const dollars = (value) => (Number.isFinite(value) ? `$${Math.round(value).toLocaleString('en-US')}` : '—');

export function centsToMoney(cents, { compact = false, signed = false } = {}) {
  if (!Number.isFinite(cents)) return '—';
  const value = cents / 100;
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : signed && value > 0 ? '+' : '';
  const body = compact && abs >= 10000 ? `${(abs / 1000).toFixed(1)}K` : compact && abs >= 1000 ? `${(abs / 1000).toFixed(2)}K` : Math.round(abs).toLocaleString('en-US');
  return `${sign}$${body}`;
}

export const percent = (value, digits = 0) => (Number.isFinite(value) ? `${value.toFixed(digits)}%` : '—');

export function parseDay(value) {
  return new Date(`${String(value).slice(0, 10)}T12:00:00`);
}

export function shortDate(value) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(parseDay(value));
}

export function longDate(value) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(parseDay(value));
}

export function rangeLabel(range) {
  if (!range) return '';
  const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(parseDay(range.end));
  return `${shortDate(range.start)} – ${shortDate(range.end)}, ${year}`;
}

export function timeLabel(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
}

export function daysBetween(start, end) {
  return Math.round((parseDay(end) - parseDay(start)) / 86400000);
}

// Dates covered by a stay's nights (arrival through the night before departure).
export function bookedDateSet(reservations) {
  const set = new Set();
  (reservations || []).forEach((reservation) => {
    const end = parseDay(reservation.departureDate);
    for (let date = parseDay(reservation.arrivalDate); date < end; date.setDate(date.getDate() + 1)) {
      set.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    }
  });
  return set;
}

export function movingAverage(values, window = 7) {
  return values.map((_, index) => {
    const slice = values.slice(Math.max(0, index - Math.floor(window / 2)), index + Math.ceil(window / 2)).filter(Number.isFinite);
    return slice.length ? slice.reduce((sum, value) => sum + value, 0) / slice.length : null;
  });
}

// Calendar cell tints for PriceLabs demand levels. Normal demand is left untinted so only the days worth noticing stand out.
export const demandTint = { Low: '#ff6f8626', Good: '#37d39a24', High: '#ffbd5c36' };

// Where a price sits inside the market's price distribution, as a percentile from 0 to 100. The market data gives the
// 25th, 50th, 75th and 90th percentile prices, so positions in between are interpolated; below the 25th it runs down
// toward 0 and above the 90th it reaches 100 at 20% over it.
export function marketPosition(price, band) {
  if (!Number.isFinite(price) || !band || ![band.p25, band.p50, band.p75, band.p90].every(Number.isFinite)) return null;
  const points = [[0, 0], [band.p25, 25], [band.p50, 50], [band.p75, 75], [band.p90, 90], [band.p90 * 1.2, 100]];
  if (price >= points[points.length - 1][0]) return 100;
  for (let index = 1; index < points.length; index += 1) {
    const [x0, y0] = points[index - 1];
    const [x1, y1] = points[index];
    if (price <= x1) return x1 > x0 ? Math.round(y0 + ((price - x0) / (x1 - x0)) * (y1 - y0)) : y1;
  }
  return 100;
}

export function positionZone(position) {
  return position < 25 ? 0 : position < 50 ? 1 : position < 75 ? 2 : 3;
}

export const ordinal = (value) => {
  const rounded = Math.round(value);
  const tail = rounded % 100;
  const suffix = tail >= 11 && tail <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[rounded % 10] || 'th';
  return `${rounded}${suffix}`;
};
